import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { innovatorAPI } from '../services/api';
import { 
  SUPPORTED_NETWORKS, 
  NETWORK_PARAMS, 
  getNetworkName,
  isNetworkSupported,
  DEFAULT_NETWORK
} from '../constants/contracts';

const WalletContext = createContext();

export function WalletProvider({ children }) {
  // Connection state
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Network state
  const [networkName, setNetworkName] = useState(null);
  const [isCorrectNetwork, setIsCorrectNetwork] = useState(false);

  // Profile state (for innovator profiles)
  const [innovatorProfile, setInnovatorProfile] = useState(null);
  const [isRegisteredInnovator, setIsRegisteredInnovator] = useState(false);

  // Innovator profile management
  const createEmptyProfile = async (address) => {
    try {
      // Normalize address to lowercase
      const normalizedAddress = address.toLowerCase();
      console.log('Creating empty profile for:', normalizedAddress);
      
      const emptyProfile = {
        walletAddress: normalizedAddress,
        roles: [],
        domains: [],
        skills: [],
        socials: {}
      };
      
      const response = await innovatorAPI.create(emptyProfile);
      setInnovatorProfile(response.data);
      setIsRegisteredInnovator(true);
      return response.data;
    } catch (error) {
      console.error('Error creating empty innovator profile:', error);
      return null;
    }
  };

  const checkInnovatorRegistration = useCallback(async (address) => {
    if (!address) return null;
    
    try {
      // Normalize address to lowercase to prevent case sensitivity issues
      const normalizedAddress = address.toLowerCase();
      console.log('Checking innovator registration for:', normalizedAddress);
      
      const response = await innovatorAPI.getByWalletAddress(normalizedAddress);
      if (response.data) {
        setInnovatorProfile(response.data);
        setIsRegisteredInnovator(true);
        return response.data;
      }
    } catch (error) {
      if (error.error?.statusCode === 404) {
        // If profile doesn't exist, create an empty one
        console.log('Profile not found, creating empty profile');
        return await createEmptyProfile(address.toLowerCase());
      }
      console.error('Error checking innovator registration:', error);
    }
    
    setInnovatorProfile(null);
    setIsRegisteredInnovator(false);
    return null;
  }, []);

  // Disconnect wallet with cleanup
  const disconnectWallet = useCallback(() => {
    console.log('Disconnecting wallet...');
    
    // Clear all wallet-related state
    setAccount(null);
    setSigner(null);
    setIsConnected(false);
    setInnovatorProfile(null);
    setIsRegisteredInnovator(false);
    
    // Reset other state
    setError(null);
    
    // Clear local storage
    localStorage.removeItem('walletAddress');
    
    // Keep provider for read-only operations but ensure it's newly created
    if (window.ethereum) {
      try {
        // Force provider refresh to clear any cached state
        const readOnlyProvider = new ethers.providers.Web3Provider(window.ethereum, "any");
        setProvider(readOnlyProvider);
        
        // Force a refresh of network info 
        readOnlyProvider.getNetwork().then(network => {
          setChainId(network.chainId);
          const networkNameValue = getNetworkName(network.chainId);
          setNetworkName(networkNameValue);
          setIsCorrectNetwork(isNetworkSupported(network.chainId));
        }).catch(err => {
          console.error('Error getting network after disconnect:', err);
        });
      } catch (error) {
        console.error('Error creating read-only provider:', error);
        setProvider(null);
      }
    }
    
    console.log('Wallet disconnected. State reset.');
  }, []);

  // Initialize provider on first load and check for existing connections
  useEffect(() => {
    const initProvider = async () => {
      try {
        if (window.ethereum) {
          console.log('Initializing wallet connection...');
          
          // Create provider and force refresh of accounts
          const web3Provider = new ethers.providers.Web3Provider(window.ethereum, "any");
          
          // Check if already connected in MetaMask by using eth_accounts
          // This gets accounts without prompting user
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          console.log('Available accounts from MetaMask:', accounts);
          
          if (accounts && accounts.length > 0) {
            // MetaMask is connected
            setProvider(web3Provider);
            
            // Always get the signer to ensure we have the active account
            const signer = web3Provider.getSigner();
            const currentAccount = await signer.getAddress();
            const normalizedAccount = currentAccount.toLowerCase();
            
            console.log('Active account from signer:', currentAccount);
            
            // Get network info
            const network = await web3Provider.getNetwork();
            
            // Set connection state
            setAccount(normalizedAccount);
            setChainId(network.chainId);
            setSigner(signer);
            setIsConnected(true);
            
            // Update network state
            const networkNameValue = getNetworkName(network.chainId);
            setNetworkName(networkNameValue);
            setIsCorrectNetwork(isNetworkSupported(network.chainId));
            
            // Check innovator registration
            await checkInnovatorRegistration(currentAccount);
            
            // Update local storage
            localStorage.setItem('walletAddress', normalizedAccount);
            
            // If not on the default network, switch to it
            if (networkNameValue !== DEFAULT_NETWORK) {
              console.log(`Wallet connected but on wrong network. Switching to ${DEFAULT_NETWORK}...`);
              await switchNetwork(DEFAULT_NETWORK);
            }
          } else {
            // Not connected via MetaMask
            console.log('No active MetaMask connection found');
            // Still set the provider for read-only operations
            setProvider(web3Provider);
            // Explicitly clear any stale localStorage data
            localStorage.removeItem('walletAddress');
            setIsConnected(false);
          }
        }
      } catch (error) {
        console.error('Error initializing wallet connection:', error);
        setError('Failed to initialize wallet connection');
      }
    };
    
    initProvider();
  }, [checkInnovatorRegistration]);

  // Handle MetaMask events (account changes, network changes, disconnect)
  useEffect(() => {
    if (!window.ethereum) return;
    
    // Account changed handler
    const handleAccountsChanged = async (accounts) => {
      console.log('MetaMask accountsChanged event detected:', accounts);
      
      if (!accounts || accounts.length === 0) {
        // User disconnected wallet through MetaMask
        console.log('No accounts available - disconnecting');
        disconnectWallet();
        return;
      }
      
      try {
        // Always update to the new account, regardless of previous state
        const newAccount = accounts[0];
        
        // Skip update if the account is the same (ignoring case)
        if (account && newAccount && account.toLowerCase() === newAccount.toLowerCase()) {
          console.log('Account unchanged (ignoring case differences):', newAccount);
          return;
        }
        
        console.log('Switching to account:', newAccount, '(previous was:', account, ')');
        
        // Create a completely fresh provider to ensure we get the latest state
        const freshProvider = new ethers.providers.Web3Provider(window.ethereum, "any");
        
        // Get the signer to verify the account
        const newSigner = freshProvider.getSigner();
        const verifiedAddress = await newSigner.getAddress();
        
        console.log('Verified signer address:', verifiedAddress);
        
        // Double check that the account from the event matches what the signer reports
        if (verifiedAddress.toLowerCase() !== newAccount.toLowerCase()) {
          console.warn(`Account mismatch: Event says ${newAccount} but signer says ${verifiedAddress}`);
          // Trust the signer over the event data
          const normalized = newAccount.toLowerCase();
          setAccount(normalized);
          localStorage.setItem('walletAddress', normalized);
        } else {
          // Update state with new account
          setAccount(newAccount);
          localStorage.setItem('walletAddress', newAccount);
        }
        
        // Update provider and signer
        setProvider(freshProvider);
        setSigner(newSigner);
        
        // Ensure connection state is updated
        setIsConnected(true);
        
        // Check innovator registration for new account
        const addressToUse = verifiedAddress.toLowerCase() !== newAccount.toLowerCase() 
          ? verifiedAddress 
          : newAccount;
        await checkInnovatorRegistration(addressToUse);
        
        // Get network info to update that as well
        const network = await freshProvider.getNetwork();
        setChainId(network.chainId);
        const networkNameValue = getNetworkName(network.chainId);
        setNetworkName(networkNameValue);
        const supported = isNetworkSupported(network.chainId);
        setIsCorrectNetwork(supported);
        
        // If not on the correct network, switch to the default
        if (networkNameValue !== DEFAULT_NETWORK) {
          console.log(`Account changed but on wrong network. Switching to ${DEFAULT_NETWORK}...`);
          await switchNetwork(DEFAULT_NETWORK);
        }
      } catch (error) {
        console.error('Error in accountsChanged handler:', error);
        // Don't disconnect on error, just log it
      }
    };
    
    // Chain/network changed handler
    const handleChainChanged = async (chainIdHex) => {
      console.log('Network changed:', chainIdHex);
      
      // Force refresh to avoid stale data issues
      // window.location.reload(); // Don't reload, handle gracefully
      
      const newChainId = parseInt(chainIdHex, 16);
      setChainId(newChainId);
      
      // Update network information
      const networkNameValue = getNetworkName(newChainId);
      setNetworkName(networkNameValue);
      const supported = isNetworkSupported(newChainId);
      setIsCorrectNetwork(supported);
      
      // Reinitialize provider
      if (window.ethereum) {
        const newProvider = new ethers.providers.Web3Provider(window.ethereum);
        setProvider(newProvider);
        
        if (isConnected) {
          try {
            const newSigner = newProvider.getSigner();
            setSigner(newSigner);
            
            // If switched to an unsupported network, automatically switch back to default
            if (!supported) {
              console.log(`Switched to unsupported network. Switching back to ${DEFAULT_NETWORK}...`);
              setTimeout(() => switchNetwork(DEFAULT_NETWORK), 500); // Small delay to ensure UI updates first
            }
          } catch (error) {
            console.error('Error getting new signer:', error);
          }
        }
      }
    };
    
    // Set up event listeners
    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);
    window.ethereum.on('disconnect', disconnectWallet);
    
    // Clean up listeners on unmount
    return () => {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
      window.ethereum.removeListener('disconnect', disconnectWallet);
    };
  }, [account, provider, isConnected, disconnectWallet, checkInnovatorRegistration]);

  // Switch network with better error handling
  const switchNetwork = async (targetNetwork = DEFAULT_NETWORK) => {
    if (!window.ethereum) {
      const errorMessage = 'Please install MetaMask to use this application';
      setError(errorMessage);
      console.error(errorMessage);
      return false;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Get network parameters
      const params = NETWORK_PARAMS[targetNetwork];
      if (!params) {
        throw new Error(`Network parameters not found for ${targetNetwork}`);
      }
      
      // Try to switch to the network
      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: params.chainId }],
        });
      } catch (switchError) {
        // This error code indicates that the chain has not been added to MetaMask
        if (switchError.code === 4902 || switchError.message.includes('wallet_addEthereumChain')) {
          // Add the network
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [params],
          });
        } else {
          throw switchError;
        }
      }
      
      // Get updated provider, signer, and network
      const newProvider = new ethers.providers.Web3Provider(window.ethereum);
      const network = await newProvider.getNetwork();
      
      // Update state
      setProvider(newProvider);
      setChainId(network.chainId);
      
      // Update signer if connected
      if (isConnected) {
        try {
          const newSigner = newProvider.getSigner();
          setSigner(newSigner);
        } catch (error) {
          console.error('Error getting new signer:', error);
        }
      }
      
      // Get network information
      const networkNameValue = getNetworkName(network.chainId);
      setNetworkName(networkNameValue);
      setIsCorrectNetwork(isNetworkSupported(network.chainId));
      
      return true;
    } catch (error) {
      console.error('Error switching network:', error);
      setError(error.message || 'Error switching network');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Connect wallet with better error handling
  const connectWallet = async () => {
    if (typeof window.ethereum === 'undefined') {
      const errorMessage = 'Please install MetaMask to connect your wallet';
      setError(errorMessage);
      console.error(errorMessage);
      return null;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Force MetaMask to show the account selection screen
      await window.ethereum.request({
        method: 'wallet_requestPermissions',
        params: [{ eth_accounts: {} }]
      });
      
      // Now request the accounts - this should give us the currently selected account in MetaMask
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });
      
      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found. User likely rejected the connection request.');
      }
      
      const metaMaskAccount = accounts[0];
      console.log('Selected account from MetaMask:', metaMaskAccount);
      
      // Create fresh provider
      const web3Provider = new ethers.providers.Web3Provider(window.ethereum, "any");
      // Important: request accounts again to ensure we're getting the latest selection
      await web3Provider.send('eth_requestAccounts', []);
      const signer = web3Provider.getSigner();
      const signerAddress = await signer.getAddress(); // This should match accounts[0]
      
      console.log('Verified signer address:', signerAddress);
      
      // Compare addresses case-insensitively
      if (signerAddress.toLowerCase() !== metaMaskAccount.toLowerCase()) {
        console.warn(`Address case mismatch: MetaMask returned ${metaMaskAccount} but signer reports ${signerAddress}`);
      }
      
      // Get network info
      const network = await web3Provider.getNetwork();
      const normalizedAddress = signerAddress.toLowerCase();
      
      // Use the signer address as the canonical form
      setAccount(normalizedAddress);
      setProvider(web3Provider);
      setSigner(signer);
      setChainId(network.chainId);
      setIsConnected(true);
      
      // Get network information
      const networkNameValue = getNetworkName(network.chainId);
      setNetworkName(networkNameValue);
      const supported = isNetworkSupported(network.chainId);
      setIsCorrectNetwork(supported);
      
      // Check innovator registration - will use normalized address internally
      await checkInnovatorRegistration(normalizedAddress);
      
      // Save wallet address to local storage for persistence
      localStorage.setItem('walletAddress', normalizedAddress);
      
      // Always switch to Optimism Sepolia after connecting
      // Only switch if not already on Optimism Sepolia
      if (networkNameValue !== DEFAULT_NETWORK) {
        console.log(`Switching to default network: ${DEFAULT_NETWORK}`);
        await switchNetwork(DEFAULT_NETWORK);
      }
      
      return signerAddress;
    } catch (error) {
      console.error('Error connecting wallet:', error);
      setError(error.message || 'Error connecting to wallet');
      setIsConnected(false);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Format address for display with better error handling
  const formatAddress = (address) => {
    if (!address) return '';
    try {
      if (typeof address !== 'string') {
        return String(address); // Try to convert non-string to string
      }
      if (address.length < 10) return address; // Address too short to format
      return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
    } catch (error) {
      console.error('Error formatting address:', error, address);
      return '';
    }
  };

  // Check connection status - useful for session recovery
  const checkConnectionStatus = useCallback(async () => {
    if (!window.ethereum) return false;
    
    try {
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      const isConnected = accounts && accounts.length > 0;
      
      // If connected but account doesn't match our stored account,
      // update our state to match metamask's current account
      if (isConnected && account && accounts[0] && 
          accounts[0].toLowerCase() !== account.toLowerCase()) {
        console.log('Detected account mismatch:', accounts[0], 'vs', account);
        // Instead of calling handleAccountsChanged which might not be in scope,
        // update the account directly
        const normalized = accounts[0].toLowerCase();
        setAccount(normalized);
        localStorage.setItem('walletAddress', normalized);
        
        if (provider) {
          const freshProvider = new ethers.providers.Web3Provider(window.ethereum, "any");
          setProvider(freshProvider);
          const newSigner = freshProvider.getSigner();
          setSigner(newSigner);
          setIsConnected(true);
          
          // Check innovator registration for new account
          await checkInnovatorRegistration(normalized);
        }
      }
      
      return isConnected;
    } catch (error) {
      console.error('Error checking connection status:', error);
      return false;
    }
  }, [account, provider, checkInnovatorRegistration]);

  return (
    <WalletContext.Provider
      value={{
        // Connection state
        account,
        walletAddress: account, // Alias for account to maintain compatibility with existing code
        provider,
        signer,
        chainId,
        isConnected,
        loading,
        error,
        
        // Network state
        networkName,
        isCorrectNetwork,
        
        // Innovator profile
        innovatorProfile,
        isRegisteredInnovator,
        
        // Actions
        connectWallet,
        disconnectWallet,
        switchNetwork,
        checkInnovatorRegistration,
        checkConnectionStatus,
        
        // Utilities
        formatAddress,
        
        // Network constants
        SUPPORTED_NETWORKS,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
}

export default WalletContext;